import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { Avatar } from '@treams/treams-ui/common/Avatar';
import { Tooltip } from '@treams/treams-ui/common/Tooltip';
import { tokens } from '@treams/treams-ui/theme/tokens';

import { formatSalary } from '../../salary-development/formatSalary';
import { type RewardEmployee, type RewardRange, SALARY_DOMAIN } from './buildRewardFunctions';

const AVATAR_SIZE = 24;
const BOX_HEIGHT = 24;
/** Avatars closer together than this, in pixels, merge into one count bubble. */
const CLUSTER_DISTANCE = AVATAR_SIZE + 4;

type Side = 'below' | 'above';

type Person = Pick<RewardEmployee, 'id' | 'name' | 'firstName' | 'lastName'> & { salary: number };

type Cluster = { people: Person[]; position: number };

const Plot = styled.div`
	position: relative;
	margin: 0 ${AVATAR_SIZE / 2}px;
`;

const Lane = styled.div<{ $height: number }>`
	position: relative;
	height: ${(props) => props.$height}px;
`;

const Axis = styled.div`
	position: absolute;
	top: 50%;
	left: 0;
	right: 0;
	height: 2px;
	transform: translateY(-50%);
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.border.default};
`;

const AxisEnd = styled.div<{ $position: number }>`
	position: absolute;
	top: 50%;
	left: ${(props) => props.$position * 100}%;
	width: 2px;
	height: 12px;
	transform: translate(-50%, -50%);
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.border.strong};
`;

const ScaleBox = styled.div<{ $from: number; $to: number }>`
	position: absolute;
	top: 0;
	bottom: 0;
	left: ${(props) => props.$from * 100}%;
	width: ${(props) => (props.$to - props.$from) * 100}%;
	min-width: 2px;
	box-sizing: border-box;
	border: 1px solid ${tokens.info.border};
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.info.background};
`;

const Midpoint = styled.div<{ $position: number }>`
	position: absolute;
	top: 4px;
	bottom: 4px;
	width: 2px;
	left: ${(props) => props.$position * 100}%;
	transform: translateX(-50%);
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.info.icon};
`;

const Marker = styled.div<{ $position: number }>`
	position: absolute;
	top: 0;
	left: ${(props) => props.$position * 100}%;
	transform: translateX(-50%);
	line-height: 0;

	&:hover {
		z-index: 1;
	}
`;

const Count = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${AVATAR_SIZE}px;
	height: ${AVATAR_SIZE}px;
	box-sizing: border-box;
	border: 1px solid ${tokens.border.white};
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.brand.icon};
	color: ${tokens.text.inverse};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.small}px;
	font-weight: ${tokens.typography.weights.bold};
	line-height: 1;
`;

function sideOf(amount: number, range?: RewardRange): Side | undefined {
	if (range?.startAmount != null && amount < range.startAmount) return 'below';
	if (range?.endAmount != null && amount > range.endAmount) return 'above';

	return undefined;
}

function useWidth() {
	const ref = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState(0);

	useEffect(() => {
		const element = ref.current;

		if (!element) {
			return;
		}

		const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));

		observer.observe(element);
		setWidth(element.getBoundingClientRect().width);

		return () => observer.disconnect();
	}, []);

	return [ref, width] as const;
}

// Walks the people from low to high salary and merges each one into the previous
// cluster when their avatars would overlap. Without a width nothing merges.
export function clusterPeople(
	people: Person[],
	toTrack: (amount: number) => number,
	width: number,
) {
	const clusters: (Cluster & { last: number })[] = [];

	for (const person of [...people].sort((left, right) => left.salary - right.salary)) {
		const position = toTrack(person.salary);
		const previous = clusters[clusters.length - 1];

		if (previous && width > 0 && (position - previous.last) * width < CLUSTER_DISTANCE) {
			previous.people.push(person);
			previous.last = position;
			previous.position =
				previous.people.reduce((sum, member) => sum + toTrack(member.salary), 0) /
				previous.people.length;
		} else {
			clusters.push({ people: [person], position, last: position });
		}
	}

	return clusters;
}

export function SalaryRangeBar({
	label,
	range,
	people,
}: {
	label: string;
	range?: RewardRange;
	/** People with a full-time salary, drawn as avatars under the scale. */
	people: Person[];
}) {
	const { i18n } = useTranslation();
	const [ref, width] = useWidth();
	const toTrack = (amount: number) =>
		Math.min(
			1,
			Math.max(0, (amount - SALARY_DOMAIN.min) / (SALARY_DOMAIN.max - SALARY_DOMAIN.min)),
		);
	const money = (amount: number) => formatSalary(Math.round(amount), i18n.language);

	const hasScale = range?.startAmount != null && range.endAmount != null;

	const valueNow =
		hasScale && range.amount != null
			? Math.round(Math.min(range.endAmount!, Math.max(range.startAmount!, range.amount)))
			: undefined;

	return (
		<Box flex={1} minWidth={0}>
			<Plot ref={ref}>
				<Lane
					$height={BOX_HEIGHT}
					role={valueNow == null ? undefined : 'meter'}
					aria-label={label}
					aria-valuemin={hasScale ? range.startAmount : undefined}
					aria-valuemax={hasScale ? range.endAmount : undefined}
					aria-valuenow={valueNow}
				>
					<Axis />
					<AxisEnd $position={0} />
					<AxisEnd $position={1} />
					{hasScale && (
						<ScaleBox $from={toTrack(range.startAmount!)} $to={toTrack(range.endAmount!)} />
					)}
					{hasScale && (
						<Midpoint $position={toTrack((range.startAmount! + range.endAmount!) / 2)} />
					)}
				</Lane>
				{people.length > 0 && (
					<Lane $height={AVATAR_SIZE} style={{ marginTop: tokens.gap.element }}>
						{clusterPeople(people, toTrack, width).map((cluster) => {
							const [first] = cluster.people;
							const names = cluster.people
								.map((person) => `${person.name} · ${money(person.salary)}`)
								.join(', ');

							return (
								<Marker key={first.id} $position={cluster.position}>
									<Tooltip content={names} position="top" dark portal fitContent>
										{cluster.people.length > 1 ? (
											<Count aria-label={names}>{cluster.people.length}</Count>
										) : (
											<Avatar.Ring
												tone={sideOf(first.salary, range) ? 'warning' : undefined}
												aria-label={names}
											>
												<Avatar size={AVATAR_SIZE} user={first} />
											</Avatar.Ring>
										)}
									</Tooltip>
								</Marker>
							);
						})}
					</Lane>
				)}
			</Plot>
		</Box>
	);
}
